import React from 'react';
import InputOrFlatpickr from 'components/InputOrFlatpickr';
import classNames from 'classnames';
import moment from 'moment';
import Tip from 'scenes/V2/Tip';
import { Shift } from 'redux/ducks/Post/Shifts';
import { DATE_PICKER_OPTIONS, TIME_PICKER_OPTIONS, shiftDuration } from 'helpers/post';
import { hour24ToMeridiem, meridiemTo24, MILITARY_FORMAT } from 'helpers/datetime';
import { DeleteButton } from 'components/V2/Buttons';
import ConfirmationModal from 'components/V2/ConfirmationModal';
import DeleteModal from 'components/V2/ConfirmationModal/DeleteModal';
import LongShiftAlert from 'scenes/V2/LongShiftAlert';
import { get } from 'lodash';

type Props = {
  idx: number,
  mapIdx: number,
  shift: Shift,
  hasBottomBorder: boolean,
  canDelete: boolean,
  wasValidated: boolean,
  dateDisabled: boolean,
  onChangeField: Function,
  onDeleteShift: Function,
  timezone: string,
  user?: any,
  eventName?: string,
  editMode?: boolean,
  props: any,
};

const OneTimeShift: React.FC<Props> = ({
  idx,
  mapIdx,
  shift,
  hasBottomBorder,
  canDelete,
  wasValidated,
  dateDisabled,
  onChangeField,
  onDeleteShift,
  timezone,
  user,
  eventName,
  editMode,
  ...props
}) => {
  const startDateRef = React.useRef(null);
  const [validated, validateField] = React.useState(INITIAL_VALIDATION_STATE);
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);

  const showError = (field) => wasValidated && !!shift.errorMessages[field];
  const onFieldBlur = (field) => () => validateField({ ...validated, [field]: true });
  const onCapacity = (amount) => onChangeField('capacity', amount);
  const onFlatpickr = (name) => (value) => onChangeField(name, value);
  const onTimeFlatpickr = (name) => (value) => onChangeField(name, meridiemTo24(value));
  const handleDeleteModal = () => setOpenDeleteModal(!openDeleteModal);
  const onScheduleChange = (e) => onChangeField('schedule', e.target.value);

  const addStaff = (amount) => () => {
    const newCapacity = Math.max(1, Number(shift.capacity) + amount);
    onCapacity(newCapacity);
  };

  const renderStartHours = () => (
    <div className="col-md-4">
      <label htmlFor="start">Start Time</label>
      <div className="input-group">
        <InputOrFlatpickr
          name="start"
          value={hour24ToMeridiem(shift.start)}
          options={TIME_PICKER_OPTIONS}
          type="datetime"
          onValueUpdate={onTimeFlatpickr('start')}
          onBlur={onFieldBlur('start')}
          className={classNames('form-control fbr nimbus-regular-font', {
            'is-invalid': showError('start'),
          })}
          placeholder="12:00 PM"
          timezone={timezone}
        />
        <div className="invalid-feedback">{shift.errorMessages['start']}</div>
      </div>
    </div>
  );

  const renderEndHours = () => (
    <div className="col-md-4">
      <label htmlFor="end">End Time</label>
      <div className="input-group">
        <InputOrFlatpickr
          name="end"
          value={hour24ToMeridiem(shift.end)}
          options={TIME_PICKER_OPTIONS}
          type="datetime"
          onValueUpdate={onTimeFlatpickr('end')}
          onBlur={onFieldBlur('end')}
          className={classNames('form-control fbr nimbus-regular-font', {
            'is-invalid': showError('end'),
          })}
          placeholder="2:00 PM"
          timezone={timezone}
        />
        <div className="invalid-feedback">{shift.errorMessages['end']}</div>
      </div>
    </div>
  );

  return (
    <div className={classNames('one-time-shift', { 'bottom-line': hasBottomBorder })} id={`s-${idx}`}>
      <div className="shiftName gotham-bold-font">Shift {mapIdx + 1}</div>

      <div className="row">
        <div className="col-md-4">
          <label htmlFor="startDate">{shift.isMultiDay ? 'Start Date' : 'Shift Date'}</label>
          <div className={classNames('input-group', { disabled: dateDisabled })}>
            <InputOrFlatpickr
              ref={startDateRef}
              type="datetime"
              name="startDate"
              data-shiftIdx={idx}
              value={shift.startDate}
              options={DATE_PICKER_OPTIONS}
              onChange={onFlatpickr('startDate')}
              onBlur={onFieldBlur('startDate')}
              className={classNames('form-control lbr nimbus-regular-font', {
                'is-invalid': showError('startDate'),
              })}
              placeholder={moment().format('MM-DD-YYYY')}
            />
            <div className="input-group-append">
              <span
                className="input-group-text rbr cursor-pointer"
                onClick={() => {
                  const input = document.querySelector(`input[data-shiftIdx="${idx}"]`);
                  if (input) input.focus();
                }}
              >
                <i className="far fa-calendar-alt" />
              </span>
            </div>
            <div className="invalid-feedback">{shift.errorMessages['startDate']}</div>
          </div>
        </div>

        {shift.isMultiDay && (
          <div className="col-md-4">
            <label htmlFor="endDate">End Date</label>
            <div className={classNames('input-group', { disabled: dateDisabled })}>
              <InputOrFlatpickr
                type="datetime"
                name="endDate"
                value={shift.endDate}
                options={DATE_PICKER_OPTIONS}
                onChange={onFlatpickr('endDate')}
                onBlur={onFieldBlur('endDate')}
                className={classNames('form-control lbr nimbus-regular-font', {
                  'is-invalid': showError('endDate'),
                })}
                placeholder={moment().format('MM-DD-YYYY')}
              />
              <div className="input-group-append">
                <span
                  className="input-group-text rbr cursor-pointer"
                  onClick={() => {
                    const input = document.querySelector(`input[name="endDate"]`);
                    if (input) input.focus();
                  }}
                >
                  <i className="far fa-calendar-alt" />
                </span>
              </div>
              <div className="invalid-feedback">{shift.errorMessages['endDate']}</div>
            </div>
          </div>
        )}
      </div>

      <div className="row mt-3">
        {renderStartHours()}
        {renderEndHours()}
      </div>

      {Number(shiftDuration(shift)) > 0 && shift.start && shift.end &&
        <div className={classNames("row col-md-12 mt-3", {
          'mb-3': Number(shiftDuration(shift)) > 8
        })}>
          <div className="total-hours">
            <p>Total shift time: {shiftDuration(shift)} hrs</p>
          </div>
        </div>
      }

      {Number(shiftDuration(shift)) > 8 && shift.start && shift.end && (
        <div className="row">
          <div className="col-md-8">
            <LongShiftAlert />
          </div>
        </div>
      )}

      <div className={classNames("row mt-3")}>
        <div className="col-md-12">
          <label htmlFor="capacity">How many athletic trainers do you need for this shift?</label>
        </div>
        <div className="col-md-4">
          <div className="input-group">
            <div className="input-group-append">
              <span onClick={addStaff(-1)} className="input-group-text lbr remove-border-r">
                <i className="fas fa-minus" />
              </span>
            </div>
            <input
              type="number"
              name="capacity"
              value={shift.capacity}
              onChange={(e) => onCapacity(e.target.value)}
              onBlur={onFieldBlur('capacity')}
              className={classNames('form-control text-center pl-4 gotham-bold-font', {
                'is-invalid': showError('capacity'),
              })}
              placeholder="1"
              min={1}
            />
            <div className="input-group-append">
              <span onClick={addStaff(1)} className="input-group-text rbr">
                <i className="fas fa-plus" />
              </span>
            </div>
            <div className="invalid-feedback">{shift.errorMessages['capacity']}</div>
          </div>
        </div>
      </div>

      {idx === 0 && (
        <div className="row pt-2">
          <div className="col-md-8">
            <Tip text="It's recommended to have 1 AT for every 2 fields" />
          </div>
        </div>
      )}

      <div className="row mt-3">
        <div className="col-md-12 custom-textarea">
          <label htmlFor="schedule">Additional Schedule Details</label>
          <textarea
            className="form-control nimbus-regular-font"
            name="schedule"
            value={shift.schedule || get(shift, 'supplies.scheduleDetails', '')}
            onChange={onScheduleChange}
            rows={2}
          />
        </div>
      </div>

      <DeleteButton show={canDelete} className="pt-3" onClick={handleDeleteModal}>
        DELETE SHIFT
      </DeleteButton>

      {editMode ? (
        <DeleteModal
          isOpen={openDeleteModal}
          title="Delete Shift"
          body={`Deleting this shift on ${moment(shift.startDate, MILITARY_FORMAT).format(
            'MM/DD/YYYY'
          )} will remove athletic trainers who applied or confirmed to work.`}
          toggle={handleDeleteModal}
          onDelete={onDeleteShift}
          item={shift}
          user={user}
          eventName={eventName}
        />
      ) : (
        <ConfirmationModal
          isOpen={openDeleteModal}
          toggle={handleDeleteModal}
          title="Delete Shift"
          body={`Are you sure you want to delete this shift on ${moment(shift.startDate, MILITARY_FORMAT).format(
            'MM/DD/YYYY'
          )}?`}
          onSave={onDeleteShift}
          cancelText="Go Back"
          confirmText="Delete"
        />
      )}
    </div>
  );
};

export default OneTimeShift;

const INITIAL_VALIDATION_STATE = {
  start: false,
  end: false,
  capacity: false,
  startDate: false,
  endDate: false,
};
